import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ConnectingScreen, ConnectionBanner, LoginScreen, useConnectionState } from './components/Connection';
import { PublicScreen, takePublicRoute, type PublicRoute } from './components/Public';
import { TitleBar } from './components/TitleBar';
import { Toasts } from './components/Toasts';
import { client, shell } from './lib/bridge';
import { applyFont, applyTheme, registerUiFont, savedFont } from './lib/theme';
import { useLanguage } from './lib/i18n';
import './theme.css';
import './app.css';

registerUiFont();
applyTheme('system');
applyFont(savedFont());
document.documentElement.classList.toggle('desktop', !!shell);

/** Invite / reset password / forgot password links (read before client.init, then removed from the address bar). */
const initialRoute = takePublicRoute();

/** Connect to the backend first (local mode waits for the child process to start; remote / web may need to log in first), then mount the main UI. */
function Root() {
  // Redraw the whole tree when the language changes (all text comes from t() at render time)
  useLanguage();
  const state = useConnectionState();
  const [ready, setReady] = useState(false);
  const [everOnline, setEverOnline] = useState(false);
  const [route, setRoute] = useState<PublicRoute | null>(initialRoute);
  useEffect(() => {
    void client.init().finally(() => setReady(true));
  }, []);
  // Opening an invite / reset link while the page is already open: only the # in the address bar changes, the page doesn't reload
  useEffect(() => {
    const onHash = () => {
      const r = takePublicRoute();
      if (r) setRoute(r);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    // Desktop remote mode has a local replica: enter the main UI even if the server is unreachable (a banner at the top says it's reconnecting)
    if (state === 'online' || (state === 'reconnecting' && client.replica())) setEverOnline(true);
    if (state === 'auth-required') setEverOnline(false);
  }, [state]);

  const bare = (body: React.ReactNode) => (
    <div className="app">
      <TitleBar syncing={false} onSync={() => {}} onSettings={() => {}} onCompose={() => {}} onShortcuts={() => {}} minimal />
      {body}
      <Toasts />
    </div>
  );
  // These pages need no login and still show when already logged in (accepting an invite switches to the new account)
  if (route) return bare(<PublicScreen route={route} onDone={() => setRoute(null)} />);
  if (!ready) return bare(<div className="app app--loading"><span className="spinner spinner--big" /></div>);
  if (state === 'auth-required') return bare(<LoginScreen onForgot={() => setRoute({ kind: 'forgot' })} />);
  if (!everOnline) return bare(<ConnectingScreen />);
  return (
    <>
      <App banner={<ConnectionBanner state={state} />} />
    </>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
